/** biome-ignore-all lint/suspicious/noExplicitAny: ESLint migration */

import { QueryClientProvider } from '@tanstack/react-query'
import { httpBatchLink } from '@trpc/client/links/httpBatchLink'
import { httpLink } from '@trpc/client/links/httpLink'
import { splitLink } from '@trpc/client/links/splitLink'
import { useCallback, useEffect, useRef, useState } from 'react'
import { type UiErrorDetail, useErrorModal } from '../components/ErrorModal/ErrorModal'
import { getConfig } from '../config'
import { trpc } from '../hooks/trpc'
import { useAuth } from '../hooks/useAuth'
import { useMessage } from '../utils/antd-utils'
import { rumError } from '../utils/rum'
import { type ConnectivityStatus, useOptionalConnectivity } from './ConnectivityProvider'
import { type BreezySurface, createQueryClient } from './createQueryClient'
import { useNetworkToast } from './NetworkToastProvider'

const config = getConfig()

type TrpcWrapperProps = { children: JSX.Element; surface?: BreezySurface }

type DedupeEntry = {
  seenAt: number
  ttlMs: number
}

const DEFAULT_ERROR_TOAST_DEDUPE_TTL_MS = 10_000
const ERROR_TOAST_DURATION_SECONDS = 10
const DEFAULT_ERROR_TOAST_KEY = 'trpc-error/global'

const isAbortError = (cause: unknown): boolean => {
  if (!cause || typeof cause !== 'object') return false
  const maybeError = cause as { name?: string }
  return maybeError.name === 'AbortError'
}

export const TrpcWrapper: React.FC<TrpcWrapperProps> = ({ children, surface = 'office' }) => {
  const auth = useAuth()
  const connectivity = useOptionalConnectivity()
  const networkToast = useNetworkToast()
  const message = useMessage()

  const errorModal = useErrorModal()
  const statusRef = useRef<ConnectivityStatus>(connectivity?.status ?? 'healthy')
  const recentToastDedupeRef = useRef<Map<string, DedupeEntry>>(new Map())

  useEffect(() => {
    if (connectivity?.status) {
      statusRef.current = connectivity.status
    }
  }, [connectivity?.status])

  const getConnectivityStatus = useCallback(() => statusRef.current, [])
  const pruneExpiredToastDedupeEntries = useCallback((now: number) => {
    const dedupeMap = recentToastDedupeRef.current
    for (const [key, entry] of dedupeMap.entries()) {
      if (now - entry.seenAt >= entry.ttlMs) {
        dedupeMap.delete(key)
      }
    }
  }, [])

  const pushToast = useCallback(
    (error: UiErrorDetail) => {
      const now = Date.now()
      pruneExpiredToastDedupeEntries(now)

      const dedupeKey = error.dedupeKey ?? DEFAULT_ERROR_TOAST_KEY
      const ttlMs = error.dedupeTtlMs ?? DEFAULT_ERROR_TOAST_DEDUPE_TTL_MS

      const existing = recentToastDedupeRef.current.get(dedupeKey)
      const firstShownAt = existing?.seenAt ?? now
      if (!existing) {
        recentToastDedupeRef.current.set(dedupeKey, { seenAt: now, ttlMs })
      }

      // Hard-cap display time: even if we re-open with the same key, we won't extend beyond ttlMs.
      const remainingMs = ttlMs - (now - firstShownAt)
      if (remainingMs <= 0) return
      const durationSeconds = Math.min(ERROR_TOAST_DURATION_SECONDS, remainingMs / 1000)

      const title = error.title
      const content = title ? `${title}: ${error.content}` : error.content

      message.open({
        key: dedupeKey,
        type: 'error',
        content,
        duration: durationSeconds,
      })
    },
    [message, pruneExpiredToastDedupeEntries],
  )

  const telemetry = useCallback(
    (error: unknown, context: { kind: 'query' | 'mutation'; meta?: { errorPolicy?: string; dedupeKey?: string } }) => {
      if (
        error &&
        typeof error === 'object' &&
        'cause' in error &&
        isAbortError((error as { cause?: unknown }).cause)
      ) {
        return
      }

      const httpStatus = (() => {
        if (error && typeof error === 'object' && 'data' in error) {
          const maybeData = (error as { data?: { httpStatus?: number } }).data
          if (maybeData && typeof maybeData.httpStatus === 'number') return maybeData.httpStatus
        }

        if (
          error &&
          typeof error === 'object' &&
          'status' in error &&
          typeof (error as { status?: unknown }).status === 'number'
        ) {
          return (error as { status: number }).status
        }

        return undefined
      })()

      const path =
        error && typeof error === 'object' && 'data' in error && (error as { data?: { path?: string } }).data?.path

      const event = httpStatus === undefined ? 'trpc_network_error' : 'trpc_http_error'
      rumError(event, error, {
        kind: context.kind,
        errorPolicy: context.meta?.errorPolicy ?? 'toast',
        dedupeKey: context.meta?.dedupeKey,
        httpStatus,
        path,
      })
    },
    [],
  )
  const [queryClient] = useState(() =>
    createQueryClient({
      surface,
      pushError: errorModal.pushError,
      pushToast,
      telemetry,
      getConnectivityStatus,
      defaultErrorPolicy: 'toast',
      onNetworkError: ({ error }) => {
        networkToast.showNetworkToast({ source: 'trpc', error, status: getConnectivityStatus() })
      },
    }),
  )
  const [trpcClient] = useState(() => {
    const url = `${config.apiUrl}/trpc`
    return trpc.createClient({
      transformer: {
        input: {
          serialize: (t: any): any => t,
          deserialize: (t: any): any => t,
        },
        output: {
          serialize: (t: any): any => t,
          deserialize: (t: any): any => t,
        },
      },
      // https://trpc.io/docs/v9/links#disable-batching-for-certain-requests
      links: [
        splitLink({
          condition(op) {
            // check for context property `skipBatch`
            return op.context.skipBatch === true
          },
          // when condition is true, use normal request
          true: httpLink({
            url,
            headers: async () => {
              return await auth.getHeaders()
            },
          }),
          // when condition is false or undefined, use batching
          false: httpBatchLink({
            url,
            headers: async () => {
              return await auth.getHeaders()
            },
          }),
        }),
      ],
    })
  })

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  )
}

TrpcWrapper.displayName = 'TrpcWrapper'
